import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';
import CloseIcon from 'js/design_system/icons/close.svg';
import { Icon } from 'js/design_system/Icon';

import routes from 'js/core_pre_webpack/routes';
import {
  DeprecatedGrid,
  GridColumn,
} from 'js/design_system/grid/DeprecatedGrid';
import getResponsivenessTraits, {
  PageName,
} from 'js/header/responsivenessTraits';
import SearchBar from 'js/header/SearchBar/SearchBar';
import { setDocumentTitleCount } from 'js/header/FocusedNav/documentTitleCount';
import ToolsSidebar from 'js/header/FocusedNav/ToolsSidebar';
import PremiumButton from 'js/header/FocusedNav/PremiumButton';
import SmallAccountMenuButton from 'js/header/FocusedNav/SmallAccountMenuButton';
import AccountMenu from 'js/header/FocusedNav/AccountMenu';
import ToolsMenu from 'js/header/FocusedNav/ToolsMenu';
import PremiumText from 'js/header/FocusedNav/PremiumText';
import { currentUser } from 'js/utils/user';
import { get } from 'js/utils/api_utils';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { useAbTest as useMobileViewToolsMenuFixAbTest } from 'js/ab_tests/tests/mobile_view_tools_menu_fix_july_2023';
import AcademiaIcon from './AcademiaIcon';
import AccountMenuButton from './AccountMenuButton';
import LanguageSwitcherModal from './LanguageSwitcherModal';
import CoralNewUserDiscountButton from './CoralNewUserDiscountButton';
import UploadButton from './UploadButton';
import AnswersMarketingButton, {
  Icon as RefutationFinderIcon,
} from '../AnswersNav/AnswersMarketingButton';
import AnswersLogoAnchor from '../AnswersNav/AnswersLogoAnchor';
import {
  IconAnchor,
  IconButton,
  IconContainer,
  IconLabel,
  NotificationDot,
} from './buttonStyles';
import {
  grantsMarketingIconButton,
  GrantsMarketingButton,
} from './GrantsMarketingButton';

import ChurnedUserReactivationButton from './churned_user_reactivation';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { AgsAiTwinMarketingButton } from './AgsAiTwinMarketingButton';
import PodcastLibraryButton from './PodcastLibraryButton';
import ImpactNavButton from './ImpactNavButton';
import _NavAndTools from "FocusedNav-NavAndTools.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=NavAndTools";
import _NavContainer from "FocusedNav-NavContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=NavContainer";
import _MobileNavContainer from "FocusedNav-MobileNavContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=MobileNavContainer";
import _MobileSearchContainer from "FocusedNav-MobileSearchContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=MobileSearchContainer";
import _WidthContainer from "FocusedNav-WidthContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=WidthContainer";
import _SearchBarContainer from "FocusedNav-SearchBarContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=SearchBarContainer";
import _IconAnchorsContainer from "FocusedNav-IconAnchorsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=IconAnchorsContainer";
import _FullViewMenu from "FocusedNav-FullViewMenu.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=FullViewMenu";
import _CloseMenuButton from "FocusedNav-CloseMenuButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=CloseMenuButton";
import _BottomMenu from "FocusedNav-BottomMenu.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=BottomMenu";


// The nav switches to the mobile style below this value (equal to $ds-tablet).
// If changing this value must also update '.focused-nav-container' styles.
export const HAMBURGER_MENU_SIZE = 768; // px
const NAV_HEIGHT_DESKTOP = 80;
const NAV_HEIGHT_MOBILE = 56;

const NavAndTools = /*#__PURE__*/styled("div", null, {
  displayName: "NavAndTools",
  styles: _NavAndTools
});

const NavContainer = /*#__PURE__*/styled("div", null, {
  displayName: "NavContainer",
  styles: _NavContainer
});

const MobileNavContainer = /*#__PURE__*/styled(NavContainer, null, {
  displayName: "MobileNavContainer",
  styles: _MobileNavContainer
});

const MobileSearchContainer = /*#__PURE__*/styled("div", null, {
  displayName: "MobileSearchContainer",
  styles: _MobileSearchContainer
});

const WidthContainer = /*#__PURE__*/styled("div", null, {
  displayName: "WidthContainer",
  styles: _WidthContainer
});

const SearchBarContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SearchBarContainer",
  styles: _SearchBarContainer
});

const IconAnchorsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "IconAnchorsContainer",
  styles: _IconAnchorsContainer
});

const FullViewMenu = /*#__PURE__*/styled("div", null, {
  displayName: "FullViewMenu",
  styles: _FullViewMenu
});

const CloseMenuButton = /*#__PURE__*/styled("button", null, {
  displayName: "CloseMenuButton",
  styles: _CloseMenuButton
});

const BottomMenu = /*#__PURE__*/styled("div", null, {
  displayName: "BottomMenu",
  styles: _BottomMenu
});

type ResponsiveIcon = {
  icon: ReactNode;
  // Lower priority wins on smaller screens.
  priority: number;
};

type ContainerProps = {
  useGrid: boolean;
  maxWidth: number | undefined;
  children: React.ReactNode;
};

const Container = ({ useGrid, maxWidth, children }: ContainerProps) =>
  useGrid ? (
    <DeprecatedGrid>
      <GridColumn sm={2} md={8} lg={12} xl={12}>
        <WidthContainer>{children}</WidthContainer>
      </GridColumn>
    </DeprecatedGrid>
  ) : (
    <WidthContainer style={{ maxWidth }}>{children}</WidthContainer>
  );

type Menu = 'account' | 'tools' | 'search';

type ChurnedUserReactivationProps = {
  offer: 'price' | 'discount';
  value: string;
  mentions?: number;
};

type Props = {
  inDevStyle?: boolean;
  pageName: PageName;
  darkMode?: boolean;
  useGrid?: boolean;
  initialQuery?: string;
  isPremium?: boolean;
  reactivatePremiumUpsellType?: string;
  halfOffCoupon?: string;
  messagesCount: number;
  hasUnseenClaimablePapers: boolean;
  hasUnseenUploadableStubs: boolean;
  hasUnseenReaders: boolean;
  coralNewUserDiscountProps?: React.ComponentProps<
    typeof CoralNewUserDiscountButton
  >;
  mobileTopIcon:
    | 'tools_top'
    | 'upsell_top'
    | 'ai_assistant_top'
    | 'podcast_library_top';
  inSocialCohort?: boolean;
  inAnswersCohort?: boolean;
  enablePrefillSearch?: boolean;
  prefillQuestionSourceWorkId?: string | number;
  trialPrice?: string;
  trialPricingCopy?: string;
  churnedUserReactivationProps?: ChurnedUserReactivationProps;
  hideSearchBar?: boolean;
  hidePremiumUpsellButton?: boolean;
  showPremiumUpsells?: boolean;
  showNotificationBadges?: boolean;
  showGrantsButtonOnNav?: boolean;
  showAiAssistantNav?: boolean;
  showImpactHubNav?: boolean;
  showPodcastLibraryNav?: boolean;
};

const FocusedNav = ({
  inDevStyle,
  pageName,
  darkMode = false,
  useGrid = false,
  initialQuery,
  isPremium = false,
  reactivatePremiumUpsellType,
  halfOffCoupon,
  messagesCount,
  hasUnseenClaimablePapers,
  hasUnseenUploadableStubs,
  hasUnseenReaders,
  coralNewUserDiscountProps,
  mobileTopIcon,
  inSocialCohort = false,
  inAnswersCohort = false,
  trialPrice,
  trialPricingCopy,
  churnedUserReactivationProps,
  hideSearchBar,
  hidePremiumUpsellButton,
  showPremiumUpsells = true,
  showNotificationBadges = true,
  showGrantsButtonOnNav = false,
  showAiAssistantNav = false,
  showImpactHubNav = false,
  showPodcastLibraryNav = false,
}: Props) => {
  const [openMenu, setOpenMenu] = useState<null | Menu>(null);
  const [searchBarFocusForced, setSearchBarFocusForced] = useState(false);
  const [showLocalizationModal, setShowLocalizationModal] = useState(false);
  const { screenWidth } = useScreenSize();
  const { maxWidth } = getResponsivenessTraits(pageName, screenWidth);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [mentionsCount, setMentionsCount] = useState(0);

  const isMobileNav = screenWidth < HAMBURGER_MENU_SIZE;

  useEffect(() => {
    const url = routes.unread_count_api_v0_notification_messages_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setNotificationCount(count));
  }, []);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setMentionsCount(count));
  }, []);

  const totalCount =
    notificationCount +
    mentionsCount +
    messagesCount +
    (hasUnseenReaders ? 1 : 0);

  // SSR-safe: Update document title in useEffect to avoid hydration mismatch
  useEffect(() => {
    if (showNotificationBadges) {
      setDocumentTitleCount(totalCount);
    }
  }, [showNotificationBadges, totalCount]);

  // Hide the skeleton placeholder when React component mounts
  useEffect(() => {
    const skeleton = document.querySelector('.focused-nav-skeleton');
    if (skeleton) {
      (skeleton as HTMLElement).style.display = 'none';
    }
  }, []);

  const startSearch = () => {
    setSearchBarFocusForced(true);
    setTimeout(() => setSearchBarFocusForced(false), 100);
  };

  const toggleMenu = useCallback(
    (menu: Menu): void => {
      if (openMenu === menu) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menu);
      }
    },
    [openMenu],
  );

  const homeButton = useMemo(
    () => (
      <IconAnchor
        key="home"
        href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
      >
        <IconContainer>
          <svg
            width="24"
            height="21"
            viewBox="0 0 24 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.6 20.4V10.8H0L12 0L24 10.8H20.4V20.4H3.6ZM12 3.228L6 8.628V18H18V8.628L12 3.228Z"
              fill="#6B6B82"
            />
          </svg>
        </IconContainer>
        <IconLabel>Home</IconLabel>
      </IconAnchor>
    ),
    [],
  );

  const impactButton = useMemo(() => <ImpactNavButton />, []);

  const refutationFinderButton = useMemo(
    () => (
      <ImpressionWatcher key="RfFinder" source="refutation_finder.nav">
        <IconAnchor
          href={routes.answers_url({
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <RefutationFinderIcon />
          </IconContainer>
          <IconLabel>Answers</IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    ),
    [],
  );

  const mentionsButton = useMemo(
    () => (
      <IconAnchor
        key="mentions"
        href={
          isPremium
            ? routes.mentions_url({ from_navbar: true, trigger: 'nav' })
            : routes.upgrade_url({
                feature: 'searchm',
                from_navbar: true,
                trigger: 'nav',
              })
        }
      >
        <IconContainer>
          <svg
            width="19"
            height="13"
            viewBox="0 0 19 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.7567 0.827148V8.82715H12.6367L10.6367 12.8271H16.3767L18.7567 8.06715V0.827148H10.7567ZM12.7567 2.82715H16.7567V7.58715L15.1367 10.8271H13.8767L15.8767 6.82715H12.7567V2.82715ZM0.756719 0.827148V8.82715H2.63672L0.636719 12.8271H6.37672L8.75672 8.06715V0.827148H0.756719ZM2.75672 2.82715H6.75672V7.58715L5.13672 10.8271H3.87672L5.87672 6.82715H2.75672V2.82715Z"
              fill="#6B6B82"
            />
          </svg>
        </IconContainer>
        <IconLabel>
          Mentions
          {mentionsCount > 0 && showNotificationBadges && <NotificationDot />}
        </IconLabel>
      </IconAnchor>
    ),
    [mentionsCount, showNotificationBadges, isPremium],
  );

  const analyticsButton = useMemo(
    () => (
      <IconAnchor
        key="analytics"
        href={routes.analytics_dashboard_url({
          page_name: currentUser?.page_name,
          subdomain: currentUser?.domain_name,
          from_navbar: true,
          trigger: 'nav',
        })}
      >
        <IconContainer>
          <svg
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.256836 5.3H3.68541V16.5H0.256836V5.3ZM6.65684 0.5H9.85684V16.5H6.65684V0.5ZM13.0568 9.64286H16.2568V16.5H13.0568V9.64286Z"
              fill="#6B6B82"
            />
          </svg>
        </IconContainer>
        <IconLabel>Analytics</IconLabel>
      </IconAnchor>
    ),
    [],
  );

  const uploadButton = useMemo(
    () => (
      <UploadButton
        key="upload"
        hasUnseenClaimablePapers={hasUnseenClaimablePapers}
        hasUnseenUploadableStubs={hasUnseenUploadableStubs}
        showNotificationBadges={showNotificationBadges}
      />
    ),
    [
      hasUnseenClaimablePapers,
      hasUnseenUploadableStubs,
      showNotificationBadges,
    ],
  );

  const toolsButton = useMemo(
    () => (
      <IconButton
        ref={toolsButtonRef}
        key="tools"
        onClick={() => toggleMenu('tools')}
        aria-pressed={openMenu === 'tools'}
      >
        <IconContainer>
          <svg
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.6406 17.0572L16.6402 17.1152L16.6982 13.1156L12.6987 13.0576L12.6406 17.0572ZM12.7277 11.0578L16.7273 11.1158L16.7853 7.11626L12.7857 7.05823L12.7277 11.0578ZM6.81536 4.97139L10.8149 5.02942L10.873 1.02984L6.87339 0.971811L6.81536 4.97139ZM12.8147 5.05844L16.8143 5.11647L16.8723 1.11689L12.8728 1.05886L12.8147 5.05844ZM6.72832 10.9708L10.7279 11.0288L10.7859 7.02921L6.78635 6.97118L6.72832 10.9708ZM0.728947 10.8837L4.72853 10.9417L4.78656 6.94216L0.786978 6.88413L0.728947 10.8837ZM0.641902 16.8831L4.64148 16.9411L4.69951 12.9415L0.699932 12.8835L0.641902 16.8831ZM6.64127 16.9701L10.6408 17.0282L10.6989 13.0286L6.6993 12.9705L6.64127 16.9701ZM0.815993 4.88434L4.81557 4.94237L4.8736 0.942796L0.874023 0.884766L0.815993 4.88434Z"
              fill="#6B6B82"
            />
          </svg>
        </IconContainer>
        <IconLabel>Tools</IconLabel>
      </IconButton>
    ),
    [openMenu, toggleMenu],
  );

  const grantsMultiplierButton = useMemo(() => grantsMarketingIconButton, []);

  const libraryButton = useMemo(
    () => (
      <ImpressionWatcher source="ags_nav.library">
        <IconAnchor
          key="library"
          href={routes.bookmarks_url({
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <Icon name="bookmark" size={24} />
          </IconContainer>
          <IconLabel>Your library</IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    ),
    [],
  );

  const aiAssistantHomeButton = useMemo(
    () => (
      <ImpressionWatcher source="ags_nav.newsfeed">
        <IconAnchor
          key="ai-assistant-home"
          href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
        >
          <IconContainer>
            <Icon name="newsmode" size={24} />
          </IconContainer>
          <IconLabel>Newsfeed</IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    ),
    [],
  );

  const icons = useMemo(() => {
    const showMentions = isPremium || showPremiumUpsells;
    const showRefutationFinder = isMobileNav && !isPremium && inAnswersCohort;
    const showGrantMultiplier =
      !isMobileNav && !isPremium && showGrantsButtonOnNav;
    const showImpactHub = showImpactHubNav && !showGrantsButtonOnNav;

    if (showAiAssistantNav) {
      return [aiAssistantHomeButton, libraryButton, toolsButton];
    }

    const unfilteredIcons: (ResponsiveIcon | false)[] = [
      inSocialCohort && { icon: homeButton, priority: 5 },
      showImpactHub && { icon: impactButton, priority: 2 },
      showMentions && { icon: mentionsButton, priority: 0 },
      showRefutationFinder && { icon: refutationFinderButton, priority: 4 },
      showGrantMultiplier && { icon: grantsMultiplierButton, priority: 4 },
      { icon: analyticsButton, priority: 1 },
      { icon: uploadButton, priority: 3 },
      { icon: toolsButton, priority: -Infinity },
    ];
    const icons: ResponsiveIcon[] = unfilteredIcons.filter(
      // Type guard is needed here to convince typescript that there are no false values
      (icon): icon is ResponsiveIcon => !!icon,
    );

    if (isPremium || hidePremiumUpsellButton) {
      return icons.map((icon) => icon.icon);
    }

    // Hide another icon every 50 pixels past the max width.
    const iconsWithPriority = icons.filter(
      (icon) => icon.priority !== undefined,
    ).length;
    const baseMaxWidth = 950; // Arbitrary pixels that respond best
    const maxWidth = baseMaxWidth + (reactivatePremiumUpsellType ? 273 : 127); // Premium button sizes
    const maxLevel =
      iconsWithPriority -
      Math.floor((maxWidth - Math.min(screenWidth, maxWidth)) / 50);
    return icons
      .filter((icon) => icon.priority <= maxLevel)
      .map((icon) => icon.icon);
  }, [
    reactivatePremiumUpsellType,
    screenWidth,
    inSocialCohort,
    inAnswersCohort,
    analyticsButton,
    refutationFinderButton,
    homeButton,
    impactButton,
    mentionsButton,
    uploadButton,
    toolsButton,
    hidePremiumUpsellButton,
    showPremiumUpsells,
    isMobileNav,
    grantsMultiplierButton,
    showGrantsButtonOnNav,
    libraryButton,
    aiAssistantHomeButton,
    showAiAssistantNav,
    showImpactHubNav,
    isPremium,
  ]);

  useScrollLock(isMobileNav && openMenu !== null);

  const closeMenuButton = (
    <CloseMenuButton onClick={() => setOpenMenu(null)}>
      <CloseIcon color="#222222" />
    </CloseMenuButton>
  );

  const positionOfFullViewMenu = useMobileViewToolsMenuFixAbTest();
  // SSR-safe: Use control variant for SSR to ensure hydration matches
  // The A/B test will update after hydration if needed
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  const isPositionFixed =
    isClient && positionOfFullViewMenu === 'fixed_position';

  if (isMobileNav) {
    const premiumUpsellButton = churnedUserReactivationProps ? (
      <ChurnedUserReactivationButton {...churnedUserReactivationProps} />
    ) : (
      <PremiumText
        coupon={halfOffCoupon}
        reactivate={reactivatePremiumUpsellType}
        coralNewUserDiscountProps={coralNewUserDiscountProps}
        trialPricingCopy={trialPricingCopy}
      />
    );

    return (
      <NavAndTools isMobileNav={isMobileNav}>
        <MobileNavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
          <Container maxWidth={maxWidth} useGrid={useGrid}>
            <SmallAccountMenuButton
              count={totalCount}
              menuOpen={openMenu === 'account'}
              setMenuOpen={(open) => setOpenMenu(open ? 'account' : null)}
              showNotificationBadges={showNotificationBadges}
            />
            <MobileSearchContainer>
              {!hideSearchBar && (
                <ImpressionWatcher source="focused_nav_search_bar">
                  <SearchBar
                    initialQuery={initialQuery}
                    darkMode={darkMode}
                    focusForced={searchBarFocusForced}
                    inAnswersCohort={inAnswersCohort}
                    showPremiumUpsells={showPremiumUpsells}
                  />
                </ImpressionWatcher>
              )}
              {hideSearchBar && <AnswersLogoAnchor />}
            </MobileSearchContainer>
            {mobileTopIcon === 'tools_top' && !inAnswersCohort && (
              <div onClick={() => toggleMenu('tools')}>{toolsButton}</div>
            )}
            {mobileTopIcon === 'tools_top' && inAnswersCohort && (
              <AnswersMarketingButton isMobileNav />
            )}
            {mobileTopIcon === 'upsell_top' && premiumUpsellButton}
            {mobileTopIcon === 'ai_assistant_top' && (
              <AgsAiTwinMarketingButton />
            )}
            {mobileTopIcon === 'podcast_library_top' && (
              <PodcastLibraryButton />
            )}
          </Container>
        </MobileNavContainer>
        {openMenu === 'tools' && (
          <FullViewMenu isPositionFixed={isPositionFixed}>
            {closeMenuButton}
            <ToolsMenu
              startSearch={startSearch}
              hasUnseenClaimablePapers={hasUnseenClaimablePapers}
              hasUnseenUploadableStubs={hasUnseenUploadableStubs}
              inAnswersCohort={inAnswersCohort}
              showPremiumTools={isPremium || showPremiumUpsells}
              showNotificationBadges={showNotificationBadges}
            />
          </FullViewMenu>
        )}
        {openMenu === 'account' && (
          <FullViewMenu isPositionFixed={isPositionFixed}>
            {closeMenuButton}
            <AccountMenu
              halfOffCoupon={halfOffCoupon}
              trialPrice={trialPrice}
              reactivate={reactivatePremiumUpsellType}
              mentionsCount={mentionsCount}
              messagesCount={messagesCount}
              showLocalizationModal={() => setShowLocalizationModal(true)}
              hasUnseenReaders={hasUnseenReaders}
              notificationCount={notificationCount}
              setOpen={(open) => setOpenMenu(open ? 'account' : null)}
              showCommunityStandards={inSocialCohort}
              showPremiumUpsells={showPremiumUpsells}
              showPremiumOptions={isPremium || showPremiumUpsells}
              showNotificationBadges={showNotificationBadges}
            />
          </FullViewMenu>
        )}
        <BottomMenu>
          {showAiAssistantNav ? (
            <>
              {aiAssistantHomeButton}
              {libraryButton}
              {toolsButton}
            </>
          ) : (
            <>
              {inSocialCohort ? (
                homeButton
              ) : (
                <IconAnchor
                  href={routes.home_url({
                    from_navbar: true,
                    trigger: 'nav',
                  })}
                >
                  <IconContainer>
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.45134 9.42666H3.35169V17.5094C3.35169 17.6847 3.42136 17.8527 3.54534 17.9766C3.66931 18.1006 3.83744 18.1701 4.01272 18.1701H8.08079H8.25579V17.9951V12.5063H10.7442V18V18.175H10.9192H14.9873C15.1626 18.175 15.3307 18.1054 15.4547 17.9815C15.5786 17.8576 15.6483 17.6895 15.6483 17.5143V9.43152H17.5487V9.43196L17.5611 9.43107C17.6864 9.42212 17.8065 9.3777 17.9074 9.303C18.0083 9.22829 18.0858 9.1264 18.1309 9.00923C18.176 8.89207 18.1868 8.76448 18.162 8.64142C18.1371 8.51835 18.0778 8.40491 17.9908 8.31437L17.9506 8.27252L17.915 8.26659L9.95616 1.02754L9.92505 0.996445L9.92507 0.996417L9.92241 0.993861C9.88874 0.961609 9.8513 0.933546 9.8109 0.910283L9.81104 0.910043L9.80225 0.90562C9.7724 0.890591 9.7414 0.877957 9.70955 0.867835C9.5656 0.810722 9.40524 0.810722 9.26129 0.867835C9.22944 0.877958 9.19844 0.890591 9.16859 0.905618L9.16837 0.905187L9.15722 0.911878C9.10329 0.944239 9.05427 0.984119 9.01164 1.0303L1.01767 8.30129L1.0174 8.30099L1.00922 8.30951C0.922243 8.40006 0.862862 8.51349 0.838039 8.63656C0.813217 8.75962 0.823982 8.88721 0.869073 9.00438C0.914164 9.12155 0.991709 9.22344 1.09262 9.29814C1.19353 9.37284 1.31363 9.41726 1.43886 9.42622L1.43883 9.42666H1.45134ZM14.9727 8.13431C14.7974 8.13431 14.6293 8.2039 14.5053 8.3278C14.3813 8.45171 14.3117 8.61978 14.3117 8.79506V16.8778H12.0225V11.8601C12.0225 11.6849 11.9529 11.5168 11.8289 11.3929C11.7049 11.269 11.5368 11.1994 11.3615 11.1994H7.59476C7.41948 11.1994 7.25135 11.269 7.12738 11.3929C7.0034 11.5168 6.93373 11.6849 6.93373 11.8601V16.8826H4.64459V8.79991C4.64459 8.62464 4.57492 8.45657 4.45094 8.33266C4.32697 8.20876 4.15884 8.13917 3.98356 8.13917H3.13247L9.48547 2.34314L15.8381 8.13431H14.9727Z"
                        fill="#6B6B82"
                        stroke="#6B6B82"
                        strokeWidth="0.35"
                      />
                    </svg>
                  </IconContainer>
                  <IconLabel>Home</IconLabel>
                </IconAnchor>
              )}
              {showImpactHubNav && !showGrantsButtonOnNav && impactButton}
              {mentionsButton}
              {analyticsButton}
              {mobileTopIcon !== 'tools_top' ? toolsButton : uploadButton}
            </>
          )}
        </BottomMenu>
        {showLocalizationModal && (
          <LanguageSwitcherModal
            onClose={() => {
              setShowLocalizationModal(false);
            }}
          />
        )}
      </NavAndTools>
    );
  }

  const premiumUpsellButton = churnedUserReactivationProps ? (
    <ChurnedUserReactivationButton {...churnedUserReactivationProps} />
  ) : (
    <PremiumButton
      coupon={halfOffCoupon}
      reactivate={reactivatePremiumUpsellType}
      coralNewUserDiscountProps={coralNewUserDiscountProps}
      trialPricingCopy={trialPricingCopy}
    />
  );

  let highlightedButton = null;
  if (showAiAssistantNav) {
    highlightedButton = 'ags_ai_twin';
  } else if (showPodcastLibraryNav) {
    highlightedButton = 'podcast_library';
  } else if (!isPremium && !hidePremiumUpsellButton) {
    highlightedButton = 'premium_upsell';
  } else if (isPremium && showGrantsButtonOnNav) {
    highlightedButton = 'grants';
  }

  return (
    <NavAndTools isMobileNav={isMobileNav}>
      <NavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
        <Container maxWidth={maxWidth} useGrid={useGrid}>
          <AcademiaIcon darkMode={darkMode} />
          <SearchBarContainer>
            {!hideSearchBar && (
              <ImpressionWatcher source="focused_nav_search_bar">
                <SearchBar
                  initialQuery={initialQuery}
                  darkMode={darkMode}
                  focusForced={searchBarFocusForced}
                  inAnswersCohort={inAnswersCohort}
                  showPremiumUpsells={showPremiumUpsells}
                />
              </ImpressionWatcher>
            )}
            {hideSearchBar && <AnswersLogoAnchor />}
          </SearchBarContainer>
          <IconAnchorsContainer>{icons}</IconAnchorsContainer>
          {highlightedButton === 'premium_upsell' && premiumUpsellButton}
          {highlightedButton === 'grants' && <GrantsMarketingButton />}
          {highlightedButton === 'ags_ai_twin' && <AgsAiTwinMarketingButton />}
          {highlightedButton === 'podcast_library' && <PodcastLibraryButton />}
          <AccountMenuButton
            halfOffCoupon={halfOffCoupon}
            reactivate={reactivatePremiumUpsellType}
            trialPrice={trialPrice}
            totalCount={totalCount}
            notificationCount={notificationCount}
            mentionsCount={mentionsCount}
            messagesCount={messagesCount}
            showLocalizationModal={() => setShowLocalizationModal(true)}
            hasUnseenReaders={hasUnseenReaders}
            open={openMenu === 'account'}
            setOpen={(open) => setOpenMenu(open ? 'account' : null)}
            showCommunityStandards={inSocialCohort}
            showPremiumUpsells={showPremiumUpsells}
            showPremiumOptions={isPremium || showPremiumUpsells}
            showNotificationBadges={showNotificationBadges}
          />
        </Container>
      </NavContainer>

      {openMenu === 'tools' && (
        <ToolsSidebar
          buttonRef={toolsButtonRef}
          close={() => setOpenMenu(null)}
          startSearch={startSearch}
          navHeight={NAV_HEIGHT_DESKTOP}
          hasUnseenClaimablePapers={hasUnseenClaimablePapers}
          hasUnseenUploadableStubs={hasUnseenUploadableStubs}
          inAnswersCohort={inAnswersCohort}
          showPremiumTools={isPremium || showPremiumUpsells}
          showNotificationBadges={showNotificationBadges}
        />
      )}

      {showLocalizationModal && (
        <LanguageSwitcherModal
          onClose={() => {
            setShowLocalizationModal(false);
          }}
        />
      )}
    </NavAndTools>
  );
};

export default FocusedNav;
